import { Search } from "lucide-react-native";
import React, { memo } from "react";

import type { EnterToSendKeyPressEvent } from "../../../utils/enterToSend";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";
import { SEMANTIC_COLORS } from "@/config/semanticColors";

/**
 * Imperative handle of the chat input, held by the container to keep the
 * input focused after an Enter-send (SE-8646).
 */
export type ChatInputHandle = React.ComponentRef<typeof TextInput>;

/**
 * Theme mode discriminator for styling lookups
 */
export type ThemeMode = "dark" | "light";

/**
 * Research toggle state discriminator for styling lookups
 */
export type ResearchState = "active" | "inactive";

/**
 * Background color classes for research toggle button based on theme and research state
 * @remarks SE-6800b: active dark:bg-blue-600/light:bg-blue-500 collapsed to
 * bg-accent-primary — a research-mode toggle is a selection/interaction
 * accent (brief rule 2), which the tool's numeric winner already matched
 * (worst=59, Δlight=[-59,-8,9], Δdark=[-27,33,20]). inactive
 * dark:bg-gray-700/light:bg-gray-200 collapsed to bg-surface-subtle
 * (worst=17, Δlight=[13,10,6], Δdark=[10,-1,-17]). Both branches now render
 * identically regardless of `themeMode`, but the theme-keyed shape is kept
 * so `RESEARCH_TEXT_COLOR` below (still genuinely theme-forked, hex icon
 * colors — out of this className-only migration's scope) stays structurally
 * paired with it.
 */
const RESEARCH_TOGGLE_BG: Record<ResearchState, Record<ThemeMode, string>> = {
  active: { dark: "bg-accent-primary", light: "bg-accent-primary" },
  inactive: { dark: "bg-surface-subtle", light: "bg-surface-subtle" },
};

/**
 * Text and icon colors for research toggle based on theme and research state
 */
const RESEARCH_TEXT_COLOR: Record<ResearchState, Record<ThemeMode, string>> = {
  active: {
    dark: SEMANTIC_COLORS["content-on-accent"],
    light: SEMANTIC_COLORS["content-on-accent"],
  },
  inactive: { dark: "#9ca3af", light: "#6b7280" },
};

/**
 * Suggested question configuration for chat input
 */
interface SuggestedQuestion {
  icon: string;
  text: string;
  query: string;
}

/**
 * Props for ChatInputView
 */
interface ChatInputViewProps {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  /**
   * Key-press handler for the input. Container-supplied: it owns the
   * Enter-to-send decision (SE-8646).
   */
  onKeyPress?: (event: EnterToSendKeyPressEvent) => void;
  /** Ref for the input, so the container can keep it focused (SE-8646). */
  inputRef?: React.Ref<ChatInputHandle>;
  onMessageSent?: () => void;
  canSend: boolean;
  isDark: boolean;
  isResearchMode: boolean;
  onToggleResearch: () => void;
  isResearchToggleEnabled: boolean;
  placeholder?: string;
  showSuggestedQuestions?: boolean;
  suggestedQuestions?: SuggestedQuestion[];
  /**
   * Builds the per-question press handler. Container-supplied so the View
   * creates no closures of its own (SE-8019).
   */
  createQuestionPressHandler?: (query: string) => () => void;
  onToggleSuggestedQuestions?: () => void;
  /** Focus the TextInput on mount (mobile-only — SE-5033 keyboard-on-arrive). */
  autoFocus?: boolean;
  /**
   * Derived by `ChatInputContainer` (SE-8019).
   */
  readonly themeMode: ThemeMode;
  /**
   * Derived by `ChatInputContainer` (SE-8019).
   */
  readonly researchState: ResearchState;
}

/**
 * View component for chat input with text field, suggested questions, and research toggle
 * @param root0 - Component props
 * @param root0.value - Current input text value
 * @param root0.onChangeText - Callback invoked when input text changes
 * @param root0.onSend - Callback invoked when send button is pressed
 * @param root0.onKeyPress - Container-supplied key-press handler for the input (SE-8646)
 * @param root0.inputRef - Ref attached to the input (SE-8646)
 * @param root0.canSend - Whether send button should be enabled
 * @param root0.isDark - Whether dark theme is active
 * @param root0.isResearchMode - Whether deep research mode is currently enabled
 * @param root0.themeMode - Container-derived theme key for the styling tables
 * @param root0.researchState - Container-derived research-toggle state key
 * @param root0.onToggleResearch - Callback invoked when research mode toggle is pressed
 * @param root0.isResearchToggleEnabled - Whether research mode toggle button should be displayed
 * @param root0.placeholder - Placeholder text for input field
 * @param root0.showSuggestedQuestions - Whether suggested questions panel is currently expanded
 * @param root0.suggestedQuestions - Array of suggested question objects to display
 * @param root0.createQuestionPressHandler - Container-supplied per-question handler factory
 * @param root0.onToggleSuggestedQuestions - Callback invoked when suggested questions toggle is pressed
 * @param root0.autoFocus - Focus the TextInput on mount (mobile-only, SE-5033)
 */
const ChatInputViewComponent = ({
  value,
  onChangeText,
  onSend,
  onKeyPress,
  inputRef,
  canSend,
  isDark,
  isResearchMode,
  onToggleResearch,
  isResearchToggleEnabled,
  placeholder = "Ask about player performance...",
  showSuggestedQuestions = false,
  suggestedQuestions = [],
  createQuestionPressHandler,
  onToggleSuggestedQuestions,
  autoFocus,
  themeMode,
  researchState,
}: ChatInputViewProps) => (
  <Box className="border-t border-outline-default bg-surface-base">
    {/* Suggested Questions Section */}
    {suggestedQuestions.length > 0 && createQuestionPressHandler && (
      <Box className="px-3 pt-3">
        <Pressable
          onPress={onToggleSuggestedQuestions}
          className={`flex-row items-center justify-between ${
            showSuggestedQuestions ? "pb-3" : "pb-2"
          }`}
        >
          <Text variant="title-sm" className="text-content-primary">
            Suggested questions
          </Text>
          <Text
            variant="unstyled"
            className={`text-title-sm text-content-muted ${
              showSuggestedQuestions ? "rotate-180" : "rotate-0"
            }`}
          >
            ▼
          </Text>
        </Pressable>

        {showSuggestedQuestions && (
          <Box className="gap-2 pb-3">
            {suggestedQuestions.map((question, index) => (
              <Pressable
                key={index}
                onPress={createQuestionPressHandler(question.query)}
                className="rounded-lg border border-outline-default bg-surface-raised p-3"
              >
                <Box className="flex-row items-start gap-2">
                  <Text variant="unstyled" className="text-title-sm">
                    {question.icon}
                  </Text>
                  <Text variant="body" className="flex-1 text-content-primary">
                    {question.text}
                  </Text>
                </Box>
              </Pressable>
            ))}
          </Box>
        )}
      </Box>
    )}

    {/* Research Toggle */}
    {isResearchToggleEnabled && (
      <Box className="px-3 py-2">
        <Pressable
          onPress={onToggleResearch}
          className={`flex-row items-center justify-center gap-2 rounded-sm px-3 py-2 ${RESEARCH_TOGGLE_BG[researchState][themeMode]}`}
        >
          <Icon
            as={Search}
            size="md"
            stroke={RESEARCH_TEXT_COLOR[researchState][themeMode]}
          />
          <Text
            variant="body-strong"
            className={`${
              isResearchMode ? "text-content-on-accent" : "text-content-muted"
            }`}
          >
            Research {isResearchMode ? "ON" : "OFF"}
          </Text>
        </Pressable>
      </Box>
    )}
    <Box className="flex-row items-end gap-2 px-3 py-2">
      <TextInput
        value={value}
        onChangeText={onChangeText}
        ref={inputRef}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        placeholderTextColor={isDark ? "#999" : "#666"}
        autoFocus={autoFocus}
        multiline
        // `min-w-0` is the containment half of SE-7731. Multiline, so this is
        // a `<textarea>` — same automatic minimum from its UA `cols`
        // intrinsic, and no ellipsis half because it wraps rather than
        // truncating.
        className="max-h-24 min-h-9 min-w-0 flex-1 rounded-lg bg-surface-muted px-3 py-2 text-title-sm text-content-primary"
        // eslint-disable-next-line no-restricted-syntax -- web-only outlineStyle:'none' focus-ring reset; not a RN style prop, no closed token
        UNSAFE_style={
          { outlineStyle: "none" } as Record<string, string | number>
        }
      />
      {canSend && (
        <Pressable onPress={onSend} className="pb-2">
          {/* SE-5358: send-button colour is the accent-primary semantic token
                (was colors.messageBubble.userBackground = the iMessage blue). */}
          <Text variant="title-sm" className="text-accent-primary">
            Send
          </Text>
        </Pressable>
      )}
    </Box>
  </Box>
);

ChatInputViewComponent.displayName = "ChatInputView";

export default memo(ChatInputViewComponent);
